// External packages
import * as ReactAria from 'react-aria-components';
import { Link, LinkProps } from 'react-router-dom';

// Components
import { Icon } from 'components/Icons';

// Utils
import { twJoin, twMerge } from 'tailwind-merge';

type ButtonOwnProps = {
  colorScheme?: 'primary' | 'secondary' | 'tertiary';
  variant?: 'solid' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
};

type ButtonProps = ButtonOwnProps & ReactAria.ButtonProps;

const getButtonClassNames = ({
  variant,
  colorScheme,
  size,
  iconPosition,
}: Pick<ButtonProps, 'colorScheme' | 'iconPosition' | 'size' | 'variant'>) => {
  const generalClasses =
    'inline-flex items-center justify-center transition-colors cursor-pointer data-disabled:cursor-not-allowed data-pending:opacity-50 data-disabled:opacity-50 rounded-lg data-pending:cursor-not-allowed gap-2';

  const sizeClasses = {
    sm: 'px-3 h-8',
    md: 'px-6 h-10',
    lg: 'px-6 h-14',
  }[size!];

  const variantClasses = {
    primary: {
      solid:
        'bg-blue-500 text-white hover:bg-blue-600 data-disabled:bg-gray-200',
      outline:
        'data-disabled:text-gray-300 data-disabled:border-gray-300 border border-blue-500 text-blue-500 hover:border-blue-600 hover:text-blue-600',
      ghost: 'text-blue-500 hover:text-blue-600 data-disabled:text-gray-200',
    },
    secondary: {
      solid: '',
      outline:
        'data-disabled:text-gray-300 data-disabled:border-gray-300 border border-gray-600 text-gray-600 hover:border-gray-700 hover:text-gray-700',
      ghost: '',
    },
    tertiary: {
      solid: 'bg-red-500 text-white hover:bg-red-600 data-disabled:bg-gray-200',
      outline:
        'data-disabled:text-gray-300 data-disabled:border-gray-300 border border-red-500 text-red-500 hover:border-red-600 hover:text-red-600',
      ghost: 'text-red-500 hover:text-red-600 data-disabled:text-gray-200',
    },
  }[colorScheme!][variant!];

  return twJoin(
    generalClasses,
    size && sizeClasses,
    colorScheme && variant && variantClasses,
    iconPosition === 'end' && 'flex-row-reverse'
  );
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'solid',
  colorScheme = 'primary',
  size = 'md',
  icon,
  iconPosition = 'start',
  className,
  children,
  ...props
}) => {
  return (
    <ReactAria.Button
      {...props}
      className={twMerge(
        getButtonClassNames({
          variant,
          colorScheme,
          size,
          iconPosition,
        }),
        className as string
      )}
    >
      {({ isPending }) => (
        <>
          {isPending ? <Icon name="loader" className="animate-spin" /> : icon}
          {!isPending && children}
        </>
      )}
    </ReactAria.Button>
  );
};

export const ButtonIcon: React.FC<Omit<ButtonProps, 'icon'>> = ({
  variant = 'solid',
  colorScheme = 'primary',
  size = 'md',
  className,
  children,
  ...props
}) => {
  return (
    <ReactAria.Button
      {...props}
      className={twMerge(
        getButtonClassNames({
          variant,
          colorScheme,
        }),
        size === 'sm' && 'h-6 w-6',
        size === 'md' && 'h-8 w-8',
        size === 'lg' && 'h-10 w-10',
        className as string
      )}
    >
      {({ isPending }) => (
        <>
          {isPending ? (
            <Icon name="loader" className="animate-spin" />
          ) : (
            children
          )}
        </>
      )}
    </ReactAria.Button>
  );
};

export const AnchorAsButton: React.FC<
  React.ComponentProps<'a'> & ButtonOwnProps
> = ({
  variant = 'solid',
  colorScheme = 'primary',
  size = 'md',
  icon,
  iconPosition = 'start',
  className,
  children,
  ...props
}) => (
  <a
    {...props}
    className={twMerge(
      getButtonClassNames({
        variant,
        colorScheme,
        size,
        iconPosition,
      }),
      className
    )}
  >
    {icon}
    {children}
  </a>
);

export const LinkAsButton: React.FC<ButtonOwnProps & LinkProps> = ({
  variant = 'solid',
  colorScheme = 'primary',
  size = 'md',
  icon,
  iconPosition = 'start',
  className,
  children,
  ...props
}) => (
  <Link
    {...props}
    className={twMerge(
      getButtonClassNames({
        variant,
        colorScheme,
        size,
        iconPosition,
      }),
      className
    )}
  >
    {icon}
    {children}
  </Link>
);

export const PlainButton = ReactAria.Button;
